<template>
  <div class="dashboard-layout-shell bg-gray-50 dark:bg-gray-900 dark:text-gray-100 flex overflow-hidden">
    <!-- Sidebar (Upgraded to RadShell Aesthetic) -->
    <div
      :class="[
        'flex flex-col bg-white/80 dark:bg-slate-950/50 backdrop-blur-md border-r border-slate-200 dark:border-slate-800 transition-all duration-300 ease-in-out h-full z-20',
        sidebarCollapsed ? 'w-16' : 'w-64'
      ]"
    >
      <!-- Sidebar Header -->
      <div class="h-16 flex items-center justify-between px-4 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
        <div class="flex items-center overflow-hidden" :class="sidebarCollapsed ? 'hidden' : 'flex'">
          <router-link to="/" class="flex items-center gap-0 group cursor-pointer flex-shrink-0 !bg-transparent !no-underline !outline-none !border-none !text-inherit">
            <img :src="systemStore.theme === 'dark' ? '/radsuite-logo-dark.png' : '/radsuite-logo.png'" alt="RADSuite" class="h-5 w-auto block" />
            <span class="text-slate-900 dark:text-white font-bold text-xl tracking-tight">Suite</span>
          </router-link>
        </div>

        <button
          @click="toggleSidebar"
          class="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
          :class="sidebarCollapsed ? 'mx-auto' : ''"
        >
          <RadIcon :name="sidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'" :size="20" />
        </button>
      </div>

      <!-- Navigation Menu -->
      <nav class="flex-grow p-4 space-y-2 overflow-y-auto dashboard-sidebar-scrollbar" :class="sidebarCollapsed ? 'dashboard-sidebar-scrollbar-collapsed' : 'dashboard-sidebar-scrollbar-expanded'">
        <!-- Account Switcher -->
        <div class="mb-6 account-switcher">
          <div v-if="!sidebarCollapsed" class="mb-2">
            <label class="px-2 text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Account</label>
            <div class="relative mt-1 px-2">
              <button
                @click="showAccountDropdown = !showAccountDropdown"
                class="w-full flex items-center justify-between px-3 py-2 text-sm border border-slate-200 dark:border-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 focus:outline-none transition-colors bg-white dark:bg-slate-900 dark:text-slate-200 shadow-sm"
              >
                <div class="flex items-center gap-2 truncate pointer-events-none">
                  <div class="w-5 h-5 rounded bg-gradient-to-br from-rad-blue/20 to-rad-red/20 flex items-center justify-center flex-shrink-0">
                    <span class="text-[10px] font-bold text-slate-900 dark:text-white">{{ getAccountName(selectedAccountId).charAt(0) }}</span>
                  </div>
                  <span class="truncate font-medium">{{ getAccountName(selectedAccountId) }}</span>
                </div>
                <RadIcon name="ChevronsUpDown" :size="14" class="text-slate-400 ml-2 flex-shrink-0" />
              </button>

              <!-- Dropdown menu -->
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <div
                  v-if="showAccountDropdown"
                  class="absolute z-50 w-[calc(100%-1rem)] left-2 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-xl max-h-60 overflow-auto py-1"
                >
                  <button
                    v-for="accountUser in authStore.accountUsers"
                    :key="accountUser.account_id"
                    @click="selectAccount(accountUser.account_id)"
                    class="w-full px-3 py-2 text-sm text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-2"
                    :class="[
                      accountUser.account_id === selectedAccountId
                        ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-600/5'
                        : 'text-slate-700 dark:text-slate-300'
                    ]"
                  >
                    <div class="w-5 h-5 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0"
                         :class="{ 'bg-blue-600/20 text-blue-600 dark:text-blue-400': accountUser.account_id === selectedAccountId }">
                      <span class="text-[10px] font-bold">{{ getAccountName(accountUser.account_id).charAt(0) }}</span>
                    </div>
                    <span class="truncate">{{ getAccountName(accountUser.account_id) }}</span>
                  </button>
                </div>
              </transition>
            </div>
          </div>
          <div v-else class="flex justify-center mt-2">
            <button
              @click="showAccountMenu = !showAccountMenu"
              class="w-10 h-10 rounded-lg bg-gradient-to-br from-rad-blue/20 to-rad-red/20 flex items-center justify-center border border-slate-200 dark:border-slate-800 hover:opacity-80 transition-opacity"
              title="Switch Account"
            >
              <span class="text-sm font-bold text-slate-900 dark:text-white">{{ getAccountName(selectedAccountId).charAt(0) }}</span>
            </button>
          </div>
        </div>

        <div v-if="showWorkspaceChannels" class="mb-4">
          <div class="space-y-1">
            <button
              @click="toggleWorkspaceChannels()"
              class="w-full flex items-center p-2.5 rounded-lg transition-all duration-200 border"
              :class="[
                isActiveSection('account-workspace')
                  ? 'bg-blue-600/10 dark:bg-blue-400/10 border-blue-600/30 dark:border-blue-400/30 text-blue-600 dark:text-blue-400 shadow-sm hover:bg-blue-600/20 dark:hover:bg-blue-400/20'
                  : 'text-slate-700 dark:text-slate-300 border-transparent hover:bg-slate-200 dark:hover:bg-slate-800/50 hover:border-slate-300/50 dark:hover:border-slate-700/50',
                sidebarCollapsed ? 'justify-center px-0' : 'justify-between'
              ]"
            >
              <div class="flex items-center" :class="sidebarCollapsed ? 'gap-0' : 'gap-3'">
                <RadIcon
                  name="Hash"
                  :size="20"
                  :class="isActiveSection('account-workspace') ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'"
                />
                <span v-if="!sidebarCollapsed" class="text-sm" :class="{ 'font-bold': isActiveSection('account-workspace') }">Channels</span>
              </div>
              <div v-if="!sidebarCollapsed" class="flex items-center gap-1">
                <router-link
                  to="/dashboard/channels"
                  @click.stop
                  class="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
                  title="Edit Channels"
                >
                  <RadIcon name="Pencil" :size="14" />
                </router-link>
                <RadIcon
                  name="ChevronDown"
                  :size="16"
                  class="text-slate-500 transition-transform duration-200"
                  :class="{ 'rotate-180': expandedSections.includes('account-workspace') }"
                />
              </div>
            </button>

            <div
              v-if="sidebarCollapsed"
              class="space-y-1 pt-1"
            >
              <button
                v-for="channel in workspace.channels.value"
                :key="channel.id"
                @click="selectWorkspaceChannel(channel.id)"
                @contextmenu.prevent="openChannelInfo(channel)"
                @touchstart="startChannelLongPress(channel)"
                @touchend="cancelChannelLongPress"
                @touchcancel="cancelChannelLongPress"
                class="w-full flex items-center justify-center h-10 transition-all duration-200"
                :class="longPressChannelId === channel.id ? 'scale-95 opacity-80' : ''"
                :title="getWorkspaceChannelLabel(channel)"
              >
                <span class="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-md text-[11px] font-bold uppercase tracking-wide border"
                  :class="route.path === '/dashboard' && workspace.selectedChannelId.value === channel.id
                    ? 'border-blue-600/30 dark:border-blue-400/30 bg-blue-600/10 dark:bg-blue-400/10 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800/50'"
                >
                  {{ getChannelInitials(channel) }}
                </span>
              </button>
            </div>

            <div
              v-else-if="expandedSections.includes('account-workspace')"
              class="ml-4 pl-5 border-l border-slate-200 dark:border-slate-800 my-1 space-y-1 max-h-64 overflow-y-auto"
            >
              <button
                v-for="channel in workspace.channels.value"
                :key="channel.id"
                @click="selectWorkspaceChannel(channel.id)"
                @contextmenu.prevent="openChannelInfo(channel)"
                @touchstart="startChannelLongPress(channel)"
                @touchend="cancelChannelLongPress"
                @touchcancel="cancelChannelLongPress"
                class="w-full flex items-center gap-3 py-1.5 px-3 text-sm rounded-lg transition-all border text-left"
                :class="route.path === '/dashboard' && workspace.selectedChannelId.value === channel.id
                  ? 'bg-blue-600/10 dark:bg-blue-400/10 border-blue-600/30 dark:border-blue-400/30 text-blue-600 dark:text-blue-400 font-bold shadow-sm hover:bg-blue-600/20 dark:hover:bg-blue-400/20'
                  : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/30 hover:border-slate-200/50 dark:hover:border-slate-700/50'"
              >
                <span class="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-md text-[11px] font-bold uppercase tracking-wide border"
                  :class="route.path === '/dashboard' && workspace.selectedChannelId.value === channel.id
                    ? 'border-blue-600/30 dark:border-blue-400/30 bg-blue-600/10 dark:bg-blue-400/10'
                    : 'border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300'"
                >
                  {{ getChannelInitials(channel) }}
                </span>
                <span class="truncate">{{ getWorkspaceChannelLabel(channel) }}</span>
              </button>
            </div>
          </div>

          <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800"></div>
        </div>

        <!-- Navigation Items -->
        <div v-for="item in navigationItems" :key="item.name" class="space-y-1">

          <!-- External Links -->
          <a
            v-if="item.isExternal"
            :href="item.path"
            target="_blank"
            class="w-full flex items-center p-2.5 rounded-lg transition-all duration-200"
            :class="sidebarCollapsed ? 'justify-center' : 'justify-between hover:bg-slate-200 dark:hover:bg-slate-800/50 text-slate-700 dark:text-slate-300'"
          >
            <div class="flex items-center" :class="sidebarCollapsed ? 'gap-0' : 'gap-3'">
              <RadIcon :name="item.icon" :size="20" class="text-slate-400" />
              <span v-if="!sidebarCollapsed" class="text-sm font-bold">{{ item.label }}</span>
            </div>
            <RadIcon v-if="!sidebarCollapsed" name="ExternalLink" :size="12" class="text-slate-500 opacity-50" />
          </a>

          <!-- Direct Links (No Children) -->
          <router-link
            v-else-if="item.children.length === 0"
            :to="item.path"
            class="w-full flex items-center p-2.5 rounded-lg transition-all duration-200 border"
            :class="[
              isActiveSection(item.name)
                ? 'bg-blue-600/10 dark:bg-blue-400/10 border-blue-600/30 dark:border-blue-400/30 text-blue-600 dark:text-blue-400 shadow-sm hover:bg-blue-600/20 dark:hover:bg-blue-400/20'
                : 'text-slate-700 dark:text-slate-300 border-transparent hover:bg-slate-200 dark:hover:bg-slate-800/50 hover:border-slate-300/50 dark:hover:border-slate-700/50',
              sidebarCollapsed ? 'justify-center px-0' : ''
            ]"
          >
            <div class="flex items-center" :class="sidebarCollapsed ? 'gap-0' : 'gap-3'">
              <RadIcon
                :name="item.icon"
                :size="20"
                :class="isActiveSection(item.name) ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'"
              />
              <span v-if="!sidebarCollapsed" class="text-sm" :class="{ 'font-bold': isActiveSection(item.name) }">{{ item.label }}</span>
            </div>
          </router-link>

          <!-- Accordion Parents -->
          <button
            v-else
            @click="toggleSection(item.name)"
            class="w-full flex items-center p-2.5 rounded-lg transition-all duration-200 border"
            :class="[
              isActiveSection(item.name)
                ? 'bg-blue-600/10 dark:bg-blue-400/10 border-blue-600/30 dark:border-blue-400/30 text-blue-600 dark:text-blue-400 shadow-sm hover:bg-blue-600/20 dark:hover:bg-blue-400/20'
                : 'text-slate-700 dark:text-slate-300 border-transparent hover:bg-slate-200 dark:hover:bg-slate-800/50 hover:border-slate-300/50 dark:hover:border-slate-700/50',
              sidebarCollapsed ? 'justify-center px-0' : 'justify-between'
            ]"
          >
            <div class="flex items-center" :class="sidebarCollapsed ? 'gap-0' : 'gap-3'">
              <RadIcon
                :name="item.icon"
                :size="20"
                :class="isActiveSection(item.name) ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'"
              />
              <span v-if="!sidebarCollapsed" class="text-sm" :class="{ 'font-bold': isActiveSection(item.name) }">{{ item.label }}</span>
            </div>
            <RadIcon
              v-if="!sidebarCollapsed"
              name="ChevronDown"
              :size="16"
              class="text-slate-500 transition-transform duration-200"
              :class="{ 'rotate-180': expandedSections.includes(item.name) }"
            />
          </button>

          <!-- Submenus -->
          <div v-if="!sidebarCollapsed && expandedSections.includes(item.name) && item.children.length > 0" class="ml-4 pl-5 border-l border-slate-200 dark:border-slate-800 my-1 space-y-1">
            <router-link
              v-for="child in item.children"
              :key="child.name"
              :to="child.path"
              class="block py-1.5 px-3 text-sm rounded-lg transition-all border"
              :class="$route.path === child.path
                ? 'bg-blue-600/10 dark:bg-blue-400/10 border-blue-600/30 dark:border-blue-400/30 text-blue-600 dark:text-blue-400 font-bold shadow-sm hover:bg-blue-600/20 dark:hover:bg-blue-400/20'
                : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/30 hover:border-slate-200/50 dark:hover:border-slate-700/50'"
            >
              {{ child.label }}
            </router-link>
          </div>
        </div>
      </nav>
    </div>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col relative min-h-0 overflow-hidden">
      <!-- Top Bar (Upgraded to RadShell Aesthetic) -->
      <header v-if="showTopBar" class="h-16 flex items-center justify-between px-6 bg-white/80 dark:bg-slate-950/50 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 flex-shrink-0 transition-colors duration-300 relative z-50">
        <div class="flex items-center gap-4">
          <div v-if="activeWorkspaceChannel" class="flex items-center gap-3">
            <span class="inline-flex items-center justify-center min-w-[32px] h-8 px-2 rounded-md text-[11px] font-bold uppercase tracking-wide border border-blue-600/30 dark:border-blue-400/30 bg-blue-600/10 dark:bg-blue-400/10 text-blue-600 dark:text-blue-400 shadow-sm">
              {{ getChannelInitials(activeWorkspaceChannel) }}
            </span>
            <h1 class="text-sm font-bold text-slate-900 dark:text-white tracking-wide"># {{ getWorkspaceChannelLabel(activeWorkspaceChannel) }}</h1>
          </div>
          <div v-else>
            <h1 class="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-widest">{{ currentPageTitle }}</h1>
            <p class="text-xs text-slate-500 dark:text-slate-400">{{ currentPageSubtitle }}</p>
          </div>
        </div>

        <div class="flex items-center gap-4 relative">
          <!-- Client Settings Button -->
          <button
            @click="showClientSettings = true"
            class="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors focus:outline-none"
            title="Client Settings"
          >
            <RadIcon name="Settings" :size="20" />
          </button>

          <!-- Theme Toggle Button -->
          <button
            @click="systemStore.toggleTheme()"
            class="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors focus:outline-none"
            :title="systemStore.theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
          >
            <RadIcon :name="systemStore.theme === 'dark' ? 'Moon' : 'Sun'" :size="20" />
          </button>

          <button
            v-if="appVersion.updateAvailable.value"
            @click="reloadPage"
            class="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none"
            title="Reload page to update to the latest version"
          >
            <RadIcon name="RefreshCw" :size="16" />
            Reload Page
          </button>

          <!-- User Menu -->
          <div class="relative user-menu-container">
            <button
              @click.stop="showUserMenu = !showUserMenu"
              class="flex items-center gap-3 p-1 pr-3 rounded-full border border-transparent hover:bg-slate-100 dark:hover:bg-slate-800/50 dark:hover:border-slate-700 transition-all focus:outline-none"
            >
              <!-- Add pointer-events-none to children so the button always catches the click -->
              <div class="pointer-events-none flex items-center gap-3">
                <RadAvatar
                  :src="authStore.user?.avatarUrl || undefined"
                  :alt="authStore.user?.displayName || 'User'"
                  size="sm"
                />
                <div class="hidden md:block text-left">
                  <div class="text-sm font-medium text-slate-900 dark:text-slate-200 leading-none mb-1">
                    {{ authStore.user?.displayName || 'User' }}
                  </div>
                  <div class="text-xs text-slate-500 dark:text-slate-400 leading-none">
                    {{ authStore.user?.email || '' }}
                  </div>
                </div>
                <RadIcon
                  name="ChevronDown"
                  :size="16"
                  class="text-slate-400 transition-transform duration-200"
                  :class="{ 'rotate-180': showUserMenu }"
                />
              </div>
            </button>

            <!-- Dropdown menu -->
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="showUserMenu"
                class="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-slate-900 rounded-xl shadow-xl shadow-slate-900/10 dark:shadow-black/50 py-1 z-[100] origin-top-right border border-slate-200 dark:border-slate-800"
                @click.stop
              >
                <!-- Mobile Only info header -->
                <div class="px-3 py-2 border-b border-slate-100 dark:border-slate-800 mb-1 md:hidden">
                  <div class="text-sm font-medium text-slate-900 dark:text-slate-200 truncate">{{ authStore.user?.displayName }}</div>
                  <div class="text-xs text-slate-500 truncate">{{ authStore.user?.email }}</div>
                </div>
                <div class="p-1">
                  <button
                    @click="signOut"
                    class="flex items-center w-full gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 focus:outline-none transition-colors"
                  >
                    <RadIcon name="LogOut" :size="16" />
                    Sign Out
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </header>

      <!-- Page Content -->
      <main class="flex-1 flex overflow-hidden relative">
        <!-- Global Container GUI Iframe (Always mounted to prevent unloading) -->
        <div
          v-show="$route.path === '/dashboard'"
          class="flex-1 flex flex-col bg-white dark:bg-gray-900 min-h-0 relative"
        >
          <!-- Loading Overlay -->
          <div
            v-if="workspace.isLoading.value"
            class="absolute inset-0 z-20 flex items-center justify-center bg-white dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 backdrop-blur-sm transition-opacity duration-300"
          >
            <div class="text-center">
              <div class="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
              <p class="text-gray-600 dark:text-gray-300 font-medium">Loading Container GUI...</p>
            </div>
          </div>

          <!-- Dynamic Buffered Iframes -->
          <div class="relative w-full h-full overflow-hidden">
            <iframe
              v-for="buffer in workspace.buffers.value"
              :key="buffer.id"
              v-show="workspace.activeBufferId.value === buffer.id && !activeByocStatusMessage"
              :src="buffer.url"
              class="absolute inset-0 w-full h-full border-none"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
              @load="workspace.onIframeLoad(buffer.id)"
            ></iframe>

            <div v-if="activeByocStatusMessage" class="flex h-full items-center justify-center bg-slate-50 px-6 text-slate-600 dark:bg-slate-950 dark:text-slate-300">
              <section class="max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-xl dark:border-slate-800 dark:bg-slate-900">
                <div class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full" :class="activeByocStatusMessage.tone === 'warning' ? 'bg-amber-100 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400' : 'bg-purple-100 text-purple-600 dark:bg-purple-950/50 dark:text-purple-400'">
                  <RadIcon :name="activeByocStatusMessage.icon" :size="28" />
                </div>
                <h3 class="text-xl font-bold text-slate-900 dark:text-white">{{ activeByocStatusMessage.title }}</h3>
                <p class="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{{ activeByocStatusMessage.description }}</p>
                <p class="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
                  {{ activeByocStatusMessage.channelLabel }}
                </p>
              </section>
            </div>

            <!-- Empty State -->
            <div v-if="workspace.buffers.value.length === 0 || !workspace.buffers.value.some(b => b.url)" class="flex-1 flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <div class="text-center">
                <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No channel selected</h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Select a channel from the left sidebar to view the container GUI.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Router View for other pages -->
        <div v-if="$route.path !== '/dashboard'" class="flex-1 overflow-auto">
          <router-view v-slot="{ Component }">
            <component :is="Component" />
          </router-view>
        </div>
      </main>
    </div>
    <div v-if="showChannelInfoModal" class="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div class="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900 rounded-t-xl">
          <h3 class="text-lg font-bold flex items-center text-slate-900 dark:text-white">
            <RadIcon name="Hash" :size="20" class="text-blue-700 dark:text-blue-400 mr-2" />
            {{ selectedInfoChannel?.name || selectedInfoChannel?.channel_id || 'Channel Info' }}
          </h3>
          <button @click="showChannelInfoModal = false" class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors p-1 rounded-md hover:bg-slate-200 dark:hover:bg-slate-800">
            <RadIcon name="X" :size="20" />
          </button>
        </div>

        <div class="p-6 overflow-y-auto space-y-8 bg-white dark:bg-slate-950">
          <section>
            <h4 class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-3">Summary</h4>
            <div class="grid grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800">
              <div>
                <p class="text-xs text-gray-400">Project</p>
                <p class="font-medium">{{ getProjectName(selectedInfoChannel?.project_id) }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-400">Type</p>
                <p class="font-medium capitalize">{{ selectedInfoChannel?.type || 'Standard' }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-400">Visibility</p>
                <p class="font-medium">{{ selectedInfoChannel?.is_private ? 'Private' : 'Public' }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-400">Channel ID</p>
                <p class="font-mono text-xs">{{ selectedInfoChannel?.channel_id || selectedInfoChannel?.id }}</p>
              </div>
            </div>
          </section>

          <section>
            <h4 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">SSH Connection</h4>
            <div class="space-y-4">
              <div class="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4">
                <p class="text-sm text-blue-700 dark:text-blue-200">
                  To connect via SSH, you must first install <strong>wstunnel</strong> globally:
                </p>
                <div class="mt-2 flex items-center bg-white dark:bg-gray-800 p-2 rounded border border-blue-100 dark:border-blue-900">
                  <code class="text-xs flex-1">npm install -g wstunnel</code>
                  <button @click="copyToClipboard('npm install -g wstunnel')" class="text-blue-600 hover:text-blue-800 ml-2">
                    <RadIcon name="Copy" :size="16" />
                  </button>
                </div>
              </div>

              <div>
                <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">VSCode Remote SSH Config</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Add this to your <code class="bg-gray-100 dark:bg-gray-700 px-1 rounded">~/.ssh/config</code> file.
                </p>
                <div class="relative group">
                  <pre class="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto leading-relaxed">{{ getSSHConfig(selectedInfoChannel) }}</pre>
                  <button
                    @click="copyToClipboard(getSSHConfig(selectedInfoChannel))"
                    class="absolute top-2 right-2 p-2 bg-gray-800 text-gray-400 rounded hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <RadIcon name="Copy" :size="16" />
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h4 class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-3">Manage Channel</h4>
            <div v-if="selectedInfoChannel && isByocChannel(selectedInfoChannel)" class="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-xl border border-purple-200 dark:border-purple-900/50">
              <p class="text-sm font-bold text-slate-900 dark:text-white">{{ getByocStatusLabel(selectedInfoChannel) }}</p>
              <p class="text-xs text-slate-600 dark:text-slate-300 mt-1">
                This is a BYOM channel. RADSuite will not cloud-spawn or restart it. Connect or reconnect it from your local RAD daemon.
              </p>
            </div>
            <div v-else-if="selectedInfoChannel && shouldShowHostedChannelControls(selectedInfoChannel)" class="space-y-3">
              <div class="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <p class="text-sm font-bold text-slate-900 dark:text-white">Restart Channel</p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">Restart the channel service and all associated agents.</p>
                </div>
                <RadButton
                  @click="restartChannel(selectedInfoChannel)"
                  :disabled="isRestarting || isShuttingDown"
                  :loading="isRestarting"
                  variant="primary"
                >
                  Restart
                </RadButton>
              </div>

              <div class="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <p class="text-sm font-bold text-slate-900 dark:text-white">Shutdown Channel</p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">Stop the channel service. It will need to be manually started again.</p>
                </div>
                <RadButton
                  @click="shutdownChannel(selectedInfoChannel)"
                  :disabled="isRestarting || isShuttingDown"
                  :loading="isShuttingDown"
                  variant="outline"
                  class="border-red-500/50 text-red-500 hover:bg-red-500/10 dark:hover:bg-red-500/10"
                >
                  Shutdown
                </RadButton>
              </div>
            </div>
          </section>
        </div>

        <div class="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-end rounded-b-xl">
          <RadButton @click="showChannelInfoModal = false" variant="ghost">
            Close
          </RadButton>
        </div>
      </div>
    </div>
  </div>

  <!-- Client Settings Modal -->
  <ClientSettingsModal :visible="showClientSettings" @close="showClientSettings = false" />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSystemStore } from '@/stores/system'
import { showSuccess } from '@/utils/toast'
import { getByocLifecycleState, getByocStatusLabel, isByocChannel, shouldShowHostedChannelControls } from '@shared/channel-visibility'
import { RadIcon, RadAvatar, RadButton, RadBadge } from '@radsuite/design-system'
import ClientSettingsModal from './ClientSettingsModal.vue'

import {
  HomeIcon,
  UsersIcon,
  CubeIcon,
  BriefcaseIcon,
  CommandLineIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  KeyIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  DocumentTextIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/vue/24/outline'
import NavigationHeader from './NavigationHeader.vue'
import { useWorkspaceManager } from '@/workspace/workspacemanager'
import { useAppVersion } from '@/composables/useAppVersion'

const authStore = useAuthStore()
const systemStore = useSystemStore()
const workspace = useWorkspaceManager()
const route = useRoute()
const router = useRouter()
const appVersion = useAppVersion()

const sidebarCollapsed = ref(false)
const showUserMenu = ref(false)
const showAccountMenu = ref(false)
const showAccountDropdown = ref(false)
const showClientSettings = ref(false)
const selectedAccountId = ref(authStore.account?.id || '')
const expandedSections = ref<string[]>(['account-workspace', 'users', 'roles', 'projects', 'logs', 'settings'])
const showWorkspaceChannels = computed(() => Boolean(authStore.account?.id))
const isMobileViewport = ref(false)
const showTopBar = computed(() => !(route.path === '/dashboard' && sidebarCollapsed.value))
const showChannelInfoModal = ref(false)
const selectedInfoChannel = ref<any>(null)
const isRestarting = ref(false)
const isShuttingDown = ref(false)
const longPressChannelId = ref<string | null>(null)
const isWindows = typeof window !== 'undefined' && window.navigator.platform.indexOf('Win') > -1
let channelLongPressTimer: ReturnType<typeof setTimeout> | null = null

// Legacy dashboard URL computation removed - no longer needed

const isLocalDashboardDev = typeof window !== 'undefined'
  && ['localhost', '127.0.0.1'].includes(window.location.hostname)

// Add Account section to navigation items
const navigationItems = [
  {
    name: 'users',
    label: 'Users',
    icon: 'Users',
    path: '/dashboard/users',
    children: [
      { name: 'users-list', label: 'Users', path: '/dashboard/users' },
      { name: 'groups', label: 'Access Groups', path: '/dashboard/groups' },
      { name: 'invites', label: 'Invites', path: '/dashboard/invites' }
    ]
  },
  {
    name: 'roles',
    label: 'Agents',
    icon: 'Bot',
    path: '/dashboard/roles',
    children: [
      { name: 'roles', label: 'Agent Roles', path: '/dashboard/roles' },
      { name: 'channels', label: 'Channels', path: '/dashboard/channels' }
    ]
  },
  ...(isLocalDashboardDev ? [{
    name: 'lifecycle',
    label: 'Automation',
    icon: 'Zap',
    path: '/dashboard/lifecycle',
    children: [
      { name: 'triggers', label: 'Triggers', path: '/dashboard/triggers' },
      { name: 'workflows', label: 'Workflows', path: '/dashboard/workflows' }
    ]
  }] : []),
  {
    name: 'projects',
    label: 'Projects',
    icon: 'Folder',
    path: '/dashboard/projects',
    children: [
      { name: 'projects-list', label: 'Projects', path: '/dashboard/projects' },
      { name: 'git-keys', label: 'Git Keys', path: '/dashboard/git-keys' },
      { name: 'ssh-keys', label: 'SSH Keys', path: '/dashboard/ssh-keys' },
      { name: 'environments', label: 'Environments', path: '/dashboard/environments' }
    ]
  },
  ...(isLocalDashboardDev ? [{
    name: 'marketplace',
    label: 'Marketplace',
    icon: 'ShoppingBag',
    path: '/dashboard/marketplace',
    children: []
  }] : []),
  {
    name: 'logs',
    label: 'Monitoring',
    icon: 'FileText',
    path: '/dashboard/logs',
    children: [
      { name: 'logs-list', label: 'Log Explorer', path: '/dashboard/logs' }
    ]
  },
  {
    name: 'settings',
    label: 'Settings',
    icon: 'Settings',
    path: '/dashboard/settings',
    children: [
      { name: 'settings-content', label: 'General', path: '/dashboard/settings' },
      { name: 'compute-profiles', label: 'Compute Profiles', path: '/dashboard/compute-profiles' },
      { name: 'auth-tokens', label: 'Auth Tokens', path: '/dashboard/auth-tokens' },
      { name: 'models', label: 'Models', path: '/dashboard/models' },
      { name: 'billing', label: 'Billing', path: '/dashboard/billing' }
    ]
  },
  {
    name: 'help',
    label: 'Help',
    icon: 'HelpCircle',
    path: '/dashboard/help',
    children: [
      { name: 'help-content', label: 'Help', path: '/dashboard/help' }
    ]
  },
]

const prefetchableDashboardPaths = computed(() => {
  const paths = new Set<string>()

  for (const item of navigationItems) {
    if (item.path.startsWith('/dashboard/')) {
      paths.add(item.path)
    }

    for (const child of item.children) {
      if (child.path.startsWith('/dashboard/')) {
        paths.add(child.path)
      }
    }
  }

  return Array.from(paths)
})

async function prefetchDashboardRoutes() {
  const routesToPrefetch = prefetchableDashboardPaths.value
    .filter(path => path !== route.path)
    .map(path => router.resolve(path))
    .filter(resolved => typeof resolved.matched.at(-1)?.components?.default === 'function')

  for (const resolved of routesToPrefetch) {
    const componentLoader = resolved.matched.at(-1)?.components?.default
    if (typeof componentLoader !== 'function') continue

    try {
      await componentLoader()
    } catch (error) {
      console.warn('Failed to prefetch dashboard route component:', resolved.fullPath, error)
    }
  }
}

const currentPageTitle = computed(() => {
  const currentItem = navigationItems.find(item =>
    item.path === route.path || item.children.some(child => child.path === route.path)
  )
  return currentItem?.label || 'Dashboard'
})

const currentPageSubtitle = computed(() => {
  const currentChild = navigationItems.flatMap(item => item.children)
    .find(child => child.path === route.path)
  return currentChild?.label || 'Manage your team and resources'
})

const activeWorkspaceChannel = computed(() => {
  if (route.path !== '/dashboard') return null
  return workspace.channels.value.find(channel => channel.id === workspace.selectedChannelId.value) || null
})

const activeByocStatusMessage = computed(() => {
  const channel = activeWorkspaceChannel.value
  if (!channel || !isByocChannel(channel)) return null

  const lifecycleState = getByocLifecycleState(channel)
  const channelLabel = channel.channel_id || channel.name || channel.id

  if (lifecycleState === 'disconnected') {
    return {
      icon: 'WifiOff',
      tone: 'warning',
      title: 'Local channel connection is offline',
      description: 'This BYOM channel was connected before, but its local runtime is not currently reachable. Start or reconnect the channel from your local BYOM environment, then return here to continue.',
      channelLabel
    }
  }

  if (lifecycleState === 'waiting_for_client') {
    return {
      icon: 'Loader2',
      tone: 'info',
      title: 'Waiting for local BYOM connection',
      description: 'RADSuite is ready for this channel. Start the local BYOM runtime and connect it to this channel to open the preview.',
      channelLabel
    }
  }

  return null
})

function syncAppHeight() {
  if (typeof window === 'undefined') return
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight
  document.documentElement.style.setProperty('--dashboard-app-height', `${viewportHeight}px`)
}

function updateViewportState() {
  if (typeof window === 'undefined') return
  syncAppHeight()
  isMobileViewport.value = window.innerWidth < 768
  if (isMobileViewport.value) {
    sidebarCollapsed.value = true
  }
}

function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

function collapseSidebarForMobile() {
  if (isMobileViewport.value) {
    sidebarCollapsed.value = true
  }
}

function toggleWorkspaceChannels() {
  if (expandedSections.value.includes('account-workspace')) {
    expandedSections.value = expandedSections.value.filter(name => name !== 'account-workspace')
  } else {
    expandedSections.value.push('account-workspace')
  }

  if (route.path !== '/dashboard') {
    router.push('/dashboard')
    return
  }

  const targetChannelId = workspace.selectedChannelId.value || workspace.channels.value[0]?.id
  if (targetChannelId) {
    workspace.selectChannel(targetChannelId)
  }
}

async function selectWorkspaceChannel(channelId: string) {
  if (route.path !== '/dashboard') {
    await router.push('/dashboard')
  }
  await workspace.selectChannel(channelId)
  collapseSidebarForMobile()
}

function getWorkspaceChannelLabel(channel: any) {
  return channel.name || channel.channel_id || channel.id
}

function openChannelInfo(channel: any) {
  selectedInfoChannel.value = channel
  showChannelInfoModal.value = true
  cancelChannelLongPress()
}

function startChannelLongPress(channel: any) {
  cancelChannelLongPress()
  longPressChannelId.value = channel.id
  channelLongPressTimer = setTimeout(() => {
    openChannelInfo(channel)
  }, 550)
}

function cancelChannelLongPress() {
  if (channelLongPressTimer) {
    clearTimeout(channelLongPressTimer)
    channelLongPressTimer = null
  }
  longPressChannelId.value = null
}

function getChannelInitials(channel: any) {
  const label = getWorkspaceChannelLabel(channel)
  const parts = label
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }

  return label.slice(0, 2).toUpperCase()
}

function toggleSection(sectionName: string) {
  // Only toggle sections that have children
  const section = navigationItems.find(item => item.name === sectionName)
  if (!section || section.children.length === 0) {
    return // Don't do anything for sections without children
  }

  if (expandedSections.value.includes(sectionName)) {
    expandedSections.value = expandedSections.value.filter(name => name !== sectionName)
  } else {
    expandedSections.value.push(sectionName)
  }

  // Navigate to the first child of the section
  if (section.children.length > 0) {
    router.push(section.children[0].path)
  }
}

function isActiveSection(sectionName: string) {
  if (sectionName === 'account-workspace') {
    return route.path === '/dashboard'
  }
  const section = navigationItems.find(item => item.name === sectionName)
  return section?.children.some(child => child.path === route.path) || section?.path === route.path || false
}

function getAccountName(accountId: string) {
  const accountUser = authStore.accountUsers.find(au => au.account_id === accountId)
  if (!accountUser?.account_id) return 'Unknown'
  
  // For default account, show special name
  if (accountUser.account_id === '00000000-0000-0000-0000-000000000000') {
    return 'Default Account'
  }
  
  // Get account name from the account object, fallback to ID
  const accountName = accountUser.account?.name || accountUser.account_id
  const shortId = accountUser.account_id.substring(0, 8)
  return `${accountName} (${shortId})`
}

const selectAccount = async (accountId: string) => {
  selectedAccountId.value = accountId
  showAccountDropdown.value = false
  await switchAccount()
}

async function switchAccount() {
  if (selectedAccountId.value) {
    await authStore.switchAccount(selectedAccountId.value)
    await workspace.loadData()
    // Reload current route to refresh data
    router.replace({ path: route.path, query: { ...route.query, t: Date.now() } })
  }
}

function goToAccountOverview() {
  // Navigate to the main dashboard view (account overview)
  router.push('/dashboard')
  // Close any open menus
  showUserMenu.value = false
  showAccountMenu.value = false
}

function reloadPage() {
  window.location.reload()
}

async function restartChannel(channel: any) {
  if (!channel || !authStore.account?.id) return

  if (!confirm(`Are you sure you want to restart channel #${channel.name || channel.channel_id}? This will temporarily disconnect all active agents.`)) {
    return
  }

  isRestarting.value = true
  try {
    const token = authStore.session?.access_token
    const apiUrl = import.meta.env.VITE_API_URL || window.location.origin

    const response = await fetch(`${apiUrl}/api/channels/${channel.id}/restart`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Account-Id': authStore.account.id
      },
      body: JSON.stringify({ channelId: channel.channel_id || channel.id })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(errorData.error || `Failed to restart channel: ${response.status}`)
    }

    showSuccess('Channel restart initiated successfully')
    workspace.reloadChannel(channel.id, 5000)
    showChannelInfoModal.value = false
  } catch (error: any) {
    console.error('Error restarting channel:', error)
    alert(error.message)
  } finally {
    isRestarting.value = false
  }
}

async function shutdownChannel(channel: any) {
  if (!channel || !authStore.account?.id) return

  if (!confirm(`Are you sure you want to shutdown channel #${channel.name || channel.channel_id}? This will stop the service and disconnect all agents.`)) {
    return
  }

  isShuttingDown.value = true
  try {
    const token = authStore.session?.access_token
    const apiUrl = import.meta.env.VITE_API_URL || window.location.origin

    const response = await fetch(`${apiUrl}/api/channels/${channel.id}/shutdown`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Account-Id': authStore.account.id
      },
      body: JSON.stringify({ channelId: channel.channel_id || channel.id })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(errorData.error || `Failed to shutdown channel: ${response.status}`)
    }

    showSuccess('Channel shutdown initiated successfully')
    showChannelInfoModal.value = false
  } catch (error: any) {
    console.error('Error shutting down channel:', error)
    alert(error.message)
  } finally {
    isShuttingDown.value = false
  }
}

function getProjectName(projectId: string) {
  if (!projectId) return 'None'
  const project = workspace.projects.value.find((p: any) => p.id === projectId)
  return project ? project.name : 'Unknown'
}

function sanitizeSSHHostPart(value: string) {
  if (!value) return ''
  return value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
}

function getSSHHostName(channel: any) {
  if (!channel || !authStore.account?.id) return 'unknown.connect.radsuite.com'

  const accountIdNoDashes = sanitizeSSHHostPart(authStore.account.id)
  const channelIdNoDashes = sanitizeSSHHostPart(channel.channel_id || channel.id)

  return `c-${channelIdNoDashes}-${accountIdNoDashes}.connect.radsuite.com`
}

function getSSHHostAlias(channel: any) {
  if (!channel) return 'radsuite-connection'
  return `radsuite-${sanitizeSSHHostPart(channel.channel_id || channel.id || 'channel')}`
}

function getSSHConfig(channel: any) {
  if (!channel) return ''
  return `Host ${getSSHHostAlias(channel)}\n  HostName ${getSSHHostName(channel)}\n  User exec\n  Port 22\n  StrictHostKeyChecking no\n  ProxyCommand wstunnel${isWindows ? '.cmd' : ''} -v -t stdio:%h:%p wss://${getSSHHostName(channel)}\n  UserKnownHostsFile /dev/null\n  IdentityFile ~/.ssh/id_radsuite_ed25519`
}

async function copyToClipboard(text: string) {
  await navigator.clipboard.writeText(text)
  showSuccess('Copied to clipboard')
}

async function signOut() {
  console.log('DashboardLayout: SignOut button clicked')
  try {
    await authStore.signOut()
    console.log('DashboardLayout: SignOut successful, redirecting to home')
    router.push('/')
  } catch (error) {
    console.error('DashboardLayout: SignOut failed:', error)
  }
}

// Close menus when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Element
  if (!target.closest('.user-menu-container')) {
    showUserMenu.value = false
  }
  if (!target.closest('.account-menu-container')) {
    showAccountMenu.value = false
  }
  // Close account dropdown when clicking outside
  if (!target.closest('.account-switcher')) {
    showAccountDropdown.value = false
  }
}

// Close menu when pressing Escape key
const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    showUserMenu.value = false
    showAccountMenu.value = false
    showAccountDropdown.value = false
  }
}

watch(() => route.path, (path) => {
  if (path === '/dashboard' && !expandedSections.value.includes('account-workspace')) {
    expandedSections.value.push('account-workspace')
  }
  collapseSidebarForMobile()
})

onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleEscapeKey)
  window.addEventListener('resize', updateViewportState)
  window.visualViewport?.addEventListener('resize', syncAppHeight)
  window.visualViewport?.addEventListener('scroll', syncAppHeight)
  selectedAccountId.value = authStore.account?.id || ''
  updateViewportState()

  if (authStore.account?.id && workspace.channels.value.length === 0) {
    await workspace.loadData()
  }

  void prefetchDashboardRoutes()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleEscapeKey)
  window.removeEventListener('resize', updateViewportState)
  window.visualViewport?.removeEventListener('resize', syncAppHeight)
  window.visualViewport?.removeEventListener('scroll', syncAppHeight)
  document.documentElement.style.removeProperty('--dashboard-app-height')
  cancelChannelLongPress()
})
</script>

<style scoped>
.dashboard-layout-shell {
  height: 100dvh;
}

.dashboard-sidebar-scrollbar {
  scrollbar-color: rgba(148, 163, 184, 0.7) transparent;
}

.dashboard-sidebar-scrollbar-expanded {
  scrollbar-width: auto;
}

.dashboard-sidebar-scrollbar-collapsed {
  scrollbar-width: thin;
}

.dashboard-sidebar-scrollbar-expanded::-webkit-scrollbar {
  width: 14px;
}

.dashboard-sidebar-scrollbar-collapsed::-webkit-scrollbar {
  width: 6px;
}

.dashboard-sidebar-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.dashboard-sidebar-scrollbar-expanded::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.78);
  border-radius: 9999px;
  border: 3px solid transparent;
  background-clip: content-box;
}

.dashboard-sidebar-scrollbar-collapsed::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.78);
  border-radius: 9999px;
}

:global(.dark) .dashboard-sidebar-scrollbar {
  scrollbar-color: rgba(71, 85, 105, 0.85) transparent;
}

:global(.dark) .dashboard-sidebar-scrollbar-expanded::-webkit-scrollbar-thumb {
  background-color: rgba(71, 85, 105, 0.85);
}

:global(.dark) .dashboard-sidebar-scrollbar-collapsed::-webkit-scrollbar-thumb {
  background-color: rgba(71, 85, 105, 0.85);
}
</style>